import React, { useEffect, useState, useCallback } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import api from "../../../lib/api";
import { PageTitle, Card, Loading } from "../ui";
import { Modal, useFlash, errText, Label } from "./cmsShared";

const ROLES = ["admin", "author", "reviewer"];

function RoleToggle({ on, disabled, onClick, tid, children }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={tid}
      className={`rounded-full px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed ${
        on ? "bg-sapphire-deep text-parchment" : "bg-sapphire-deep/5 text-sapphire-deep/50 hover:bg-sapphire-deep/10"
      } ${disabled ? "opacity-70" : ""}`}>
      {children}
    </button>
  );
}

function AddUserModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", rank: "Master Mason", password: "", cms_roles: ["author"] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (r) => setF((s) => ({ ...s, cms_roles: s.cms_roles.includes(r) ? s.cms_roles.filter((x) => x !== r) : [...s.cms_roles, r] }));
  const save = async () => {
    setBusy(true);
    try { await api.post("/cms/users", f); onSaved(); } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  return (
    <Modal title="Add a Brother" onClose={onClose} testid="add-user-modal">
      <div className="grid gap-4">
        {[["name", "Full name"], ["email", "Email"], ["rank", "Rank"], ["password", "Temporary password"]].map(([k, l]) => (
          <label key={k}><Label>{l}</Label>
            <input className="input-lodge" type={k === "password" ? "password" : "text"} value={f[k]} onChange={up(k)} data-testid={`add-user-${k}`} />
          </label>
        ))}
        <div><Label>Content roles</Label>
          <div className="flex gap-2">{ROLES.map((r) => <RoleToggle key={r} on={f.cms_roles.includes(r)} onClick={() => toggle(r)} tid={`add-user-role-${r}`}>{r}</RoleToggle>)}</div>
        </div>
      </div>
      {err && <p className="mt-3 font-sans text-sm text-garnet" data-testid="add-user-error">{err}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <button onClick={onClose} className="btn-dark">Cancel</button>
        <button onClick={save} disabled={busy || !f.name || !f.email || f.password.length < 8} className="btn-sapphire" data-testid="add-user-save">
          {busy && <Loader2 size={15} className="animate-spin" />} Add user
        </button>
      </div>
    </Modal>
  );
}

export default function CmsUsers() {
  const [users, setUsers] = useState(null);
  const [adding, setAdding] = useState(false);
  const [flashNode, flash] = useFlash();
  const load = useCallback(() => api.get("/cms/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (u, path, body, msg) => {
    try { await api.patch(`/cms/users/${u.id}/${path}`, body); await load(); flash(msg); } catch (e) { flash(errText(e), "error"); }
  };
  const toggleRole = (u, r) => {
    const roles = u.cms_roles || [];
    const next = roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];
    patch(u, "roles", { cms_roles: next }, `Roles updated for ${u.name}.`);
  };
  if (!users) return <Loading />;

  return (
    <div data-testid="cms-users">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitle eyebrow="Content · Access" title="Users & Roles" sub="Grant Brethren content capabilities on top of their portal login." />
        <button onClick={() => setAdding(true)} className="btn-sapphire" data-testid="add-user-btn"><UserPlus size={16} /> Add user</button>
      </div>
      <Card className="overflow-x-auto !p-0">
        <table className="w-full min-w-[720px] font-sans text-sm">
          <thead>
            <tr className="border-b border-sapphire-deep/10 text-left text-[11px] uppercase tracking-widest text-stonegrey">
              <th className="px-6 py-4">Brother</th><th className="px-4 py-4">Content roles</th><th className="px-4 py-4">Access</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const portalAdmin = u.is_admin;
              const active = u.active !== false;
              return (
                <tr key={u.id} className={`border-b border-sapphire-deep/5 ${active ? "" : "opacity-50"}`} data-testid={`user-row-${u.email}`}>
                  <td className="px-6 py-4">
                    <p className="font-semibold text-sapphire-deep">{u.name}</p>
                    <p className="text-xs text-stonegrey">{u.email} · {u.rank}</p>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      {ROLES.map((r) => (
                        <RoleToggle key={r} on={portalAdmin || (u.cms_roles || []).includes(r)} disabled={portalAdmin} onClick={() => toggleRole(u, r)} tid={`role-${r}-${u.email}`}>{r}</RoleToggle>
                      ))}
                    </div>
                    {portalAdmin && <p className="mt-1 text-[11px] italic text-stonegrey">Portal administrator — full access</p>}
                  </td>
                  <td className="px-4 py-4">
                    <button disabled={portalAdmin} onClick={() => patch(u, "active", { active: !active }, active ? `${u.name} deactivated.` : `${u.name} reactivated.`)}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed ${active ? "bg-emerald/12 text-emerald" : "bg-garnet/12 text-garnet"}`}
                      data-testid={`active-toggle-${u.email}`}>{active ? "Active" : "Deactivated"}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {adding && <AddUserModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); flash("User added."); }} />}
      {flashNode}
    </div>
  );
}
